import type { IAssignmentRepository } from '@/core/domain/school/assignment/IAssignmentRepository'
import { Assignment } from '@/core/domain/school/assignment/Assignment'
import { ValidModuleForAssignment } from '@/core/domain/school/assignment/ValidModuleForAssignment'
import { AssignmentForClassroom } from '@/core/domain/school/assignment/AssignmentForClassroom'
import type { AssignmentInputEntity } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory.ts'
import { ClassroomAssignedModule } from '@/core/domain/school/assignment/ClassroomAssignedModule.ts'

export default class AssignmentRepository implements IAssignmentRepository {
  /**
   * Récupère la liste des modules valides pour la matrice d'assignment
   * GET /api/admin/schools/{schoolId}/periods/{periodId}/assignments/modules
   */
  async fetchValidModulesForAssignment(
    schoolId: number,
    periodId: number
  ): Promise<ValidModuleForAssignment[]> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${periodId}/assignments/modules`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for valid modules')
    }

    return response.data.map((module) => ValidModuleForAssignment.create(module))
  }

  /**
   * Récupère tous les assignments pour une période scolaire donnée
   * GET /api/admin/schools/{schoolId}/periods/{periodId}/assignments
   */
  async fetchAllAssignments(schoolId: number, periodId: number): Promise<Assignment[]> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${periodId}/assignments`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for assignments')
    }

    return response.data.map((assignment) => Assignment.create(assignment))
  }

  /**
   * Crée un nouvel assignment pour une période scolaire
   * POST /api/admin/schools/{schoolId}/periods/{periodId}/assignments
   */
  async createAssignment(
    schoolId: number,
    periodId: number,
    assignment: AssignmentInputEntity
  ): Promise<Assignment> {
    const response = await apiService.post(
      `admin/schools/${schoolId}/periods/${periodId}/assignments`,
      assignment
    )
    return Assignment.create(response.data)
  }

  /**
   * Met à jour un assignment existant
   * PUT /api/admin/schools/{schoolId}/periods/{periodId}/assignments/{id}
   */
  async updateAssignment(
    schoolId: number,
    periodId: number,
    assignmentId: number,
    assignment: AssignmentInputEntity
  ): Promise<Assignment> {
    const response = await apiService.put(
      `admin/schools/${schoolId}/periods/${periodId}/assignments/${assignmentId}`,
      assignment
    )
    return Assignment.create(response.data)
  }

  /**
   * Récupère l'assignment valable pour une classe donnée avec toutes les informations enrichies
   * GET /api/admin/schools/{schoolId}/periods/{periodId}/assignments/classrooms/{classroomId}
   */
  async fetchAssignmentsForClassroom(
    schoolId: number,
    periodId: number,
    classroomId: number
  ): Promise<AssignmentForClassroom | null> {
    try {
      const response = await apiService.get(
        `admin/schools/${schoolId}/periods/${periodId}/assignments/classrooms/${classroomId}`
      )
      // Si pas de données ou données vides, retourner null (pas d'assignment configuré)
      if (!response.data || Object.keys(response.data).length === 0) {
        return null
      }
      return AssignmentForClassroom.create(response.data)
    } catch (error) {
      // Si erreur 404, c'est qu'il n'y a pas d'assignment, pas une vraie erreur
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return null
      }
      // Pour les autres erreurs, les propager
      throw error
    }
  }

  /**
   * Rend visible un module donné pour l'élève
   * PUT /api/admin/schools/{schoolId}/periods/{periodId}/assignments/{id}/visible
   */
  async setModuleVisibility(
    schoolId: number,
    periodId: number,
    assignmentId: number,
    moduleId: number,
    classroomId: number,
  ): Promise<ClassroomAssignedModule> {
    const payload = {
      id: assignmentId,
      schoolPeriodId: periodId,
      learningModuleId: moduleId,
      classroomId: classroomId,
    }
    const response = await apiService.put(
      `admin/schools/${schoolId}/periods/${periodId}/assignments/${assignmentId}/visible`,
      payload
    )
    return ClassroomAssignedModule.create(response.data)
  }
}
